import dynamic from 'next/dynamic';
import { clsx } from 'clsx';

import { DISTRIBUTOR_NAME, DOMAIN } from '@autopass-web/domain';

import { MapCta2AppButton } from '../../../components/map-cta-2-app-button';
import {
  CURRENT_DISTRIBUTOR,
  distributorDeepLinkFns,
} from '../../../new-distributor-config';
import storage from '../../../service/storage';
import { useUserMe } from '../../../services/use-user-me';

import type {
  BasePoiCharacteristics,
  PinType,
} from '@autopass-web/feature-maps';
import type { Parkinglot, Poi } from '@autopass-web/types';

declare global {
  interface Window {
    host_externalUrl: (uri: string) => void;
  }
}

const deepLinkFns = distributorDeepLinkFns['RICHART_LIFE'];

const FeatureMaps = dynamic(() => import('@autopass-web/feature-maps'), {
  ssr: false,
});

export const RichartLifeMapPage = () => {
  const hasAuth = storage.getAuth();
  const { userMe, isGetUserMeLoading } = useUserMe({
    enabled: !!hasAuth,
  });

  if (isGetUserMeLoading) {
    return null;
  }

  return (
    <div className={clsx('h-full', 'w-full')}>
      <FeatureMaps
        basePath="/maps"
        isNextModule
        isParkingFreeFeatureOn
        poiCardTutorialConfig={{ isExternallyNavigable: false }}
        parsePinType={parseRichartLifePinType}
        basePoiCharacteristics={MAP_BASE_POI_CHARACTERISTICS}
        events={{
          distributorName: DISTRIBUTOR_NAME[CURRENT_DISTRIBUTOR],
        }}
        openExternalLinkHandler={window.host_externalUrl}
        externalWidgets={{
          ctaButton: !!userMe ? null : (
            <MapCta2AppButton
              distributor="RICHART_LIFE"
              onClick={() => {
                deepLinkFns.onRichartLifeAutopassPageNavigate();
              }}
            />
          ),
        }}
      />
    </div>
  );
};

const MAP_BASE_POI_CHARACTERISTICS = {
  parkinglot: {
    cooperationStates: ['in_cooperated'],
  },
} as BasePoiCharacteristics;

function parseRichartLifePinType(poi: Poi, isParkingFreeOn: boolean) {
  switch (poi.type) {
    case 'Parkinglot':
      return parseParkinglotPinType(poi, isParkingFreeOn);
    default:
      throw new Error('parseRichartLifePinType: Invalid poi type');
  }
}

function parseParkinglotPinType(
  parkinglot: Parkinglot,
  isParkingFreeOn: boolean,
): PinType {
  const { collection_ids } = parkinglot;

  const isParkinglotSupportFreeParking = collection_ids.includes(
    DOMAIN.COLLECTION_ID.RICHART_LIFE_FREE_PARKING_PARKINGLOT,
  );

  if (isParkingFreeOn && isParkinglotSupportFreeParking) {
    return 'parkinglot-autopass-price-free-parking';
  }

  return 'parkinglot-autopass';
}
